import { Layout, locale, util } from '@glu/core';
import template from './columnHeader.hbs';
import rowSelectorTemplate from './columnHeaderRowSelector.hbs';

export default Layout.extend({
  template,
  tagName: 'th',

  modelEvents: {
    'change:width': 'setWidth'
  },

  events: {
    'click @ui.allRowsSelectorCheckbox': 'handleSelectAllRows'
  },

  triggers: {
    'click .txt': 'click'
  },

  ui: {
    buttons: 'button',
    gridHeaderContent: '.grid-header-content',
    allRowsSelectorCheckbox: '.grid-all-rows-selector'
  },

  attributes() {
    const attributes = {
      'data-cid': this.model.cid,
      scope: (this.model.has('scope') ? this.model.get('scope') : 'col'),
      title: (this.model.get('title') || locale.get('columnHeader'))
    };

    if (this.model.has('sort')) {
      attributes['aria-sort'] = `${this.model.get('sorted')}ending`;
      // TODO logic with icons
    }

    return attributes;
  },

  className() {
    let className = '';

    if (this.model.has('className')) {
      className += this.model.get('className');
    }

    if (this.model.has('rowButtons') || this.model.get('type') === 'rowSelector' || this.model.get('type') === 'singleRowSelector') {
      className += ' grid-action';
    }
    return className;
  },

  initialize(options) {
    this.defaultWidth = options.defaultWidth || 'auto';
    this.gridCid = options.gridCid;
    this.grid = options.grid;
    this.masterCollection = options.masterCollection;

    if (!this.model.has('label')) {
      this.model.set('label', this.model.get('title'), {
        silent: true
      });
    }
  },

  delegateEvents(...args) {
    Layout.prototype.delegateEvents.apply(this, args);

    this.initializeEventListeners();

    const field = this.model.get('field');

    if (field && this.masterCollection) {
      this.listenTo(this.masterCollection, 'grid:filter:change', this.uncheckColumnSelect, this);

      if (this.model.has('selectAllCondition')) {
        this.listenTo(this.masterCollection, 'sync add remove reset', this.updateAllCheckboxState, this);
      }
    }
  },

  onClose() {
    this.destroyResizing();
  },

  // Conditionally change the template based on the column type
  // TODO: ideally we should introduce tableHeader subclasses.
  getTemplate() {
    return this.model.get('type') === 'rowSelector' ? rowSelectorTemplate : template;
  },

  onBeforeRender() {
    this.setColumnOptions('sortable');
    this.setColumnOptions('resizable');
    this.setColumnOptions('draggable');
  },

  onRender() {
    if (this.model.has('selectAllCondition')) {
      this.updateAllCheckboxState();
    }
    this.setWidth();

    // disable dragging and resize functionality for rowButtons
    // TODO: do we really need to disable it?
    if (this.model.has('rowButtons')) {
      return;
    }

    this.enableResizing();
  },

  handleDragStart() {
    if (this.isClosed) {
      return;
    }

    this.grid.$('table').addClass(this.resizeActiveClassName);
    this.$el.addClass('grid-resizing');
  },

  handleDragEnd(event, ui) {
    if (this.isClosed) {
      return;
    }

    const newColumnWidth = ui.size.width;

    this.$el.removeClass('grid-resizing');

    this.grid.$('table').removeClass(this.resizeActiveClassName);

    this.$el.css({
      width: newColumnWidth,
      minWidth: newColumnWidth,
      maxWidth: newColumnWidth
    });

    this.model.set({
      width: newColumnWidth
    });

    this.grid.trigger('grid:columnResizeComplete');
  },

  handleDragMove(event, ui) {
    if (this.isClosed) {
      return;
    }
    const newColumnWidth = ui.size.width;

    this.$el.css({
      width: newColumnWidth,
      minWidth: newColumnWidth,
      maxWidth: newColumnWidth
    });
  },

  templateHelpers() {
    let isSortable = this.options.sortable;
    let isDraggable = this.options.draggable;
    let isResizable = this.options.resizable;

    // Sortable class is also driven by `sortable` being false on a column
    isSortable = (isSortable && this.model.has('sortable')) ? this.model.get('sortable') : isSortable;

    // Draggable class is also driven by `draggable` being false on a column
    isDraggable = (isDraggable && this.model.has('draggable')) ? this.model.get('draggable') : isDraggable;

    // Resizable class is also driven by `resizable` being false on a column
    isResizable = (isResizable && this.model.has('resizable')) ? this.model.get('resizable') : isResizable;

    if (this.model.has('rowButtons')) {
      // disable dragging and resize functionality for rowButtons
      // TODO: do we really need to disable it
      isDraggable = false;
      isResizable = false;
    }

    return {
      id: this.cid,
      isSortable,
      isDraggable,
      isResizable,
      isLastColumn: this.isLastColumn
    };
  },

  addManageColumnsOffset() {
    this.isLastColumn = true;
    this.render();
  },

  updateAllCheckboxState() {
    const selected = this.model.get('selectAllCondition')();

    this.ui.allRowsSelectorCheckbox.prop('checked', selected);
  },

  initializeEventListeners() {
    let selectedGrid;
    let allRowsSelected = false;

    if (this.model.get('type') === 'rowSelector') {
      const allRowsSelectorClass = '.grid-all-rows-selector';
      const rowSelectorClass = '.column-row-selector';

      /**
       * Listen to row selection events.
       * The individual checkboxes are far enough away in the object graph to warrant the use of the
       * appBus to broadcast selection events. This also has the added benefit of only impacting the
       * tableHeader and checkboxCell modules.
       *
       * We are also purposely not modifying the underlying collection.
       * This implementation is designed to only
       * affect those items that are currently visible on that page.
       */
      this.listenTo(this.appBus, 'grid:selectRow', (...args) => {
        selectedGrid = args[0].gridCid;

        if (this.gridCid === selectedGrid) {
          // need to provide callback to manually maintain state of the "select all" checkbox
          if (this.model.has('selectAllCondition')) {
            return this.$(allRowsSelectorClass).prop('checked', this.model.get('selectAllCondition')());
          }

          // If we have rows in the table, see how many rows are selected in the table
          allRowsSelected = this.$el.parents('table').find(`${rowSelectorClass}:not(:checked)`).size() === 0;


          // If all rows are selected, check the "select all" box.
          this.$(allRowsSelectorClass).prop('checked', allRowsSelected);
        }
        return undefined;
      });

      /*
      * Listen to pagination events.
      * Clear the "select all" checkbox whenever a paging event is received.
      * No need to clear the individual row selectors.  We get this for free.
      */
      this.listenTo(this.appBus, 'grid:paginate', () => {
        this.$el.find(allRowsSelectorClass).prop('checked', false);
      });

      /*
      * Listen to filter events.
      * Clear checkbox whenever a filter event is received.
      */
      this.listenTo(this.appBus, 'grid:filter', () => {
        this.$el.find(allRowsSelectorClass).prop('checked', false);
        this.$el.parents('table').find(rowSelectorClass).prop('checked', false);

        if (this.masterCollection) {
          this.masterCollection.each(model => {
            model.gridRowSelected = false;
          });
        }
      });
    }
  },

  enableResizing() {
    // column's options should have highest priority and shouldn't be overridden due to cases
    // when we need to disable this functionality for specific columns such as action buttons
    if (!this.model.get('resizable') || !this.options.resizable) {
      return;
    }

    this.resizeActiveClassName = 'grid-column-resize-active';
    this.$el.resizable({
      handles: {
        // handle only east direction and use custom resize marker.
        e: this.$('.grid-column-resize-marker')
      },
      minWidth: 67,
      containment: 'parent',
      // set COL width
      start: this.handleDragStart.bind(this),
      resize: this.handleDragMove.bind(this),
      stop: this.handleDragEnd.bind(this)
    });
  },

  destroyResizing() {
    // column's options should have highest priority and shouldn't be overridden due to cases
    // when we need to disable this functionality for specific columns such as action buttons
    const isNotResizable = !this.model.get('resizable') || !this.options.resizable;

    if (!this.$el.resizable('instance') || isNotResizable || this.model.has('rowButtons')) {
      return;
    }

    this.$el.resizable('destroy');
  },

  setColumnOptions(optionName) {
    // this mapper will be used to support legacy option
    const mapper = {
      draggable: 'reordering',
      resizable: 'resizing',
      sortable: 'sorting'
    };

    const options = {
      silent: true
    };

    if (util.isUndefined(this.model.get(optionName))) {
      this.model.set(optionName, true, options);
    }

    // Support legacy option
    if (this.model.has(mapper[optionName])) {
      this.model.set(optionName, this.model.get(mapper[optionName]), options);
    }
  },

  setWidth() {
    const useModelWidth = (this.model.has('width') && this.model.get('width') !== 'auto');
    let finalWidth;

    if (useModelWidth) {
      finalWidth = this.model.get('width');
    } else if (this.options.disableDynamicColumnWidths) {
      finalWidth = 'auto';
    } else if (this.model.has('setWidth')) {
      finalWidth = this.model.get('setWidth');
    } else {
      finalWidth = this.defaultWidth;
    }

    this.$el.css({
      width: finalWidth,
      minWidth: finalWidth,
      maxWidth: finalWidth
    });

    const gridColumnWidth = useModelWidth ? 'set' : 'default';

    this.$el.data({
      initialWidth: finalWidth,
      gridColumnWidth
    });

    // When the table is finally rendered, it may be smaller than the document. If it is, then
    // any columns that were given a default width, instead instead be sized by the browser.
    // We add an attribute so that we can grab those columns as required in `tableLayout.js`.
    this.$el.attr('data-grid-column-width', gridColumnWidth);
  },

  // Broadcast the row selection event.
  handleSelectAllRows(e) {
    e.stopPropagation();
    // TODO Replace this with an internal/private bus for the grid
    this.appBus.trigger(`grid:internal:selectAllRows:${this.gridCid}`, this);
    return e;
  },

  uncheckColumnSelect() {
    const isRowSelector = this.model.get('type') === 'rowSelector';

    if (!isRowSelector) {
      return;
    }

    this.ui.allRowsSelectorCheckbox.prop('checked', false);
  }
});
